import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, HTML_LANG, OG_LOCALE, stripLanguagePrefix, localizePath } from '../i18n/languages';
import COUNTRY_NAMES from '../data/countryNames.json';

// Default meta content used as fallback per route - optimized for SEO and CTR
const ROUTE_META = {
    '/': {
        title: 'WecoBook: Global Economic Data & Country Indicators Platform',
        description: 'Explore GDP, inflation, wages, unemployment & exchange rates data from countries worldwide. Compare economic indicators with our interactive tools.',
        type: 'website',
        keywords: 'GDP growth rate, inflation rate, unemployment rate, economic indicators, country data, global economics, Wecobook'
    },
    '/map': {
        title: 'World Economic Map: Visualize GDP, Inflation & Indicators by Country',
        description: 'Interactive map showing GDP, inflation, interest rates & exchange rates by country. Discover global economic trends and patterns instantly.',
        type: 'website',
        keywords: 'economic map, world map, GDP by country, inflation map, international economics, economic data visualization'
    },
    '/compare': {
        title: 'Compare Countries: GDP, Inflation, Wages & Economic Indicators Side-by-Side',
        description: 'Compare economic indicators between any two countries including GDP growth, inflation rates, wage levels & unemployment. Easy side-by-side analysis.',
        type: 'website',
        keywords: 'compare countries, country comparison, GDP comparison, inflation comparison, economic indicators, international economics'
    },
    '/correlation': {
        title: 'Economic Indicator Correlation Analysis: Discover Relationships Between GDP, Inflation & More',
        description: 'Analyze how GDP, inflation, exchange rates and other economic indicators correlate and move together over time with lag detection capabilities.',
        type: 'website',
        keywords: 'economic correlation, GDP inflation correlation, indicator relationships, lag analysis, regression analysis, economic relationships'
    },
    '/About': {
        title: 'About WecoBook: Your Source for Global Economic Data & Analysis',
        description: 'Learn about WecoBook - the platform that collects & analyzes economic data from central banks & statistics offices worldwide.',
        type: 'website',
        keywords: 'about Wecobook, economic data platform, global economics, economic indicators, economic analysis'
    }
};

// Site base URL — keep in sync with deployment domain
const SITE_URL = 'https://www.wecobook.com';

// Maps a route path to its key in the "seo" block of every locale file
const ROUTE_SEO_KEY = {
    '/': 'home',
    '/pulse': 'pulse',
    '/calendar': 'calendar',
    '/map': 'map',
    '/compare': 'compare',
    '/correlation': 'correlation',
    '/About': 'about',
    '/forecast-accuracy': 'forecastAccuracy'
};

// Build breadcrumb list items based on current path
function buildBreadcrumbs(path) {
    const parts = path.split('/').filter(Boolean);
    const items = [
        {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": SITE_URL + "/"
        }
    ];

    let accumulatedPath = '';
    parts.forEach((part, index) => {
        accumulatedPath += '/' + part;
        const label = part.charAt(0).toUpperCase() + part.slice(1);
        items.push({
            "@type": "ListItem",
            "position": index + 2,
            "name": label,
            "item": SITE_URL + accumulatedPath
        });
    });

    return items;
}

export default function SEO({ title, description, type, author, keywords }: { title?: string; description?: string; type?: string; author?: string; keywords?: string }) {
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const fullPath = location.pathname === '/' ? '/' : location.pathname.replace(/\/+$/, '');
    // Route metadata is keyed by the language-independent path ('/es/map' -> '/map')
    const path = stripLanguagePrefix(fullPath);

    const language = SUPPORTED_LANGUAGES.includes(i18n.language) ? i18n.language : DEFAULT_LANGUAGE;
    const htmlLang = HTML_LANG[language];

    // Resolve effective values: explicit props override translated route
    // metadata, which overrides the English route defaults
    const seoKey = ROUTE_SEO_KEY[path] || ROUTE_SEO_KEY['/'];
    const translatedTitle = i18n.exists(`seo.${seoKey}.title`) ? t(`seo.${seoKey}.title`) : null;
    const translatedDescription = i18n.exists(`seo.${seoKey}.description`) ? t(`seo.${seoKey}.description`) : null;

    const routeMeta = ROUTE_META[path] || ROUTE_META['/'] || {};
    const effectiveTitle = title || translatedTitle || routeMeta.title || 'WecoBook';
    const effectiveDescription = description || translatedDescription || routeMeta.description || '';
    const effectiveType = type || routeMeta.type || 'website';
    const effectiveKeywords = keywords || routeMeta.keywords || '';
    const effectiveAuthor = author || 'Anton Nalyvaiko';
    // Pages whose own component renders Helmet title/description (countries,
    // guides, blog, studio, briefing) must not also get the Seo fallback:
    // react-helmet-async v3 on React 19 hoists every rendered <title> into
    // <head> as its own element, so with two Helmet instances mounted both
    // titles persist and the first one in document order wins - the Seo
    // fallback, leaving those pages with the homepage title in prerendered
    // snapshots (which the postbuild duplicate-title gate then rejects).
    const PAGE_OWNED_META = /^\/(countries|guides|studio)\/[^/]+|^\/blog(\/|$)|^\/briefing\/?$/;
    const defersPageMeta = PAGE_OWNED_META.test(path);

    // Canonical points at the current language version of the page
    const canonicalUrl = `${SITE_URL}${localizePath(path, language)}`;
    // Every language version of the current route, for hreflang annotations
    const languageAlternates = SUPPORTED_LANGUAGES.map((lng) => ({
        lng,
        href: `${SITE_URL}${localizePath(path, lng)}`
    }));

    // Generate JSON-LD structured data for the current page
    let jsonLd = null;
    const jsonLdArray = [];

    // BreadcrumbList schema for all pages (enhanced with proper structure)
        const breadcrumbItems = buildBreadcrumbs(path);
        // Ensure we have a proper Home item as first breadcrumb. The check must
        // read `item` - the property buildBreadcrumbs actually sets - because
        // `@id` is undefined there, so the previous version unshifted a second
        // Home on every page ("1. Home -> 2. Home -> ...").
        if (breadcrumbItems.length === 0 || breadcrumbItems[0].item !== SITE_URL + '/') {
            breadcrumbItems.unshift({
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": SITE_URL + "/"
            });
        }
        // Re-number positions
        const positionedBreadcrumbItems = breadcrumbItems.map((item, index) => ({
            ...item,
            "position": index + 1
        }));
    
        jsonLdArray.push({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": positionedBreadcrumbItems
        });
    
        // Organization schema for knowledge graph. Only verifiable
        // properties are emitted: fabricated contact data (address,
        // phone) and links to profiles that do not exist are treated
        // by Google as a spam signal.
        jsonLdArray.push({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "WecoBook",
            "url": SITE_URL,
            "logo": {
                "@type": "ImageObject",
                "url": SITE_URL + "/favicon.ico",
                "width": 32,
                "height": 32
            },
            "foundingDate": "2020",
            "founder": {
                "@type": "Person",
                "name": "Anton Nalyvaiko",
                "url": "https://antonnalivayko1.medium.com"
            },
            "description": "WecoBook is a comprehensive platform for tracking and analyzing economic data from countries worldwide."
        });

    if (path.startsWith('/countries/') && path.length > '/countries/'.length) {
        // Country page structured data
        const countryCode = path.substring('/countries/'.length).toUpperCase();
        // Country pages defer their title/description to CountryFullFinInfo, so
        // `effectiveTitle` falls back to the catalog route meta here. Deriving
        // the entity name from it published the catalog title as the country
        // name. Use the canonical static map instead - the same source the
        // country page uses for its own Dataset schema, and prerender-safe.
        const countryName = COUNTRY_NAMES[countryCode] || countryCode;
        jsonLd = {
            "@context": "https://schema.org",
            "@type": "Country",
            "name": countryName,
            "alternateName": countryCode,
            "identifier": countryCode,
            "additionalProperty": [
                {
                    "@type": "PropertyValue",
                    "name": "economicIndicators",
                    "description": "GDP, inflation, wage data for " + countryName
                }
            ],
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": canonicalUrl
            }
        };
    } else if (path === '/' || path === '/map' || path === '/compare' || path === '/correlation') {
        // Site-wide dataset structured data for main pages
        jsonLd = {
            "@context": "https://schema.org",
            "@type": "DataCatalog",
            "name": effectiveTitle,
            "description": effectiveDescription,
            "url": canonicalUrl,
            "provider": {
                "@type": "Organization",
                "name": "WecoBook",
                "url": SITE_URL,
                "logo": {
                    "@type": "ImageObject",
                    "url": SITE_URL + "/favicon.ico"
                }
            },
            "potentialAction": {
                "@type": "SearchAction",
                "target": SITE_URL + "/?q={search_term_string}",
                "query-input": "required name=search_term_string"
            },
            // Every entry carries creator and license: Google reports missing
            // Dataset fields as enhancement items, and they can become
            // critical later.
            "dataset": [
                {
                    "@type": "Dataset",
                    "name": "GDP Indicators",
                    "description": "Gross Domestic Product data from World Bank, IMF, OECD and national statistics offices, with historical series and current values by country.",
                    "url": SITE_URL + "/map",
                    "license": "https://creativecommons.org/licenses/by/4.0/",
                    "creator": { "@type": "Organization", "name": "WecoBook", "url": SITE_URL },
                    "isAccessibleForFree": true
                },
                {
                    "@type": "Dataset",
                    "name": "Inflation Indicators",
                    "description": "Consumer Price Index and inflation data from national statistics offices, with historical series and current values by country.",
                    "url": SITE_URL + "/map",
                    "license": "https://creativecommons.org/licenses/by/4.0/",
                    "creator": { "@type": "Organization", "name": "WecoBook", "url": SITE_URL },
                    "isAccessibleForFree": true
                },
                {
                    "@type": "Dataset",
                    "name": "Exchange Rates",
                    "description": "Foreign exchange rates from central banks and national statistics offices, with historical series and current values by country.",
                    "url": SITE_URL + "/correlation",
                    "license": "https://creativecommons.org/licenses/by/4.0/",
                    "creator": { "@type": "Organization", "name": "WecoBook", "url": SITE_URL },
                    "isAccessibleForFree": true
                }
            ]
        };
    }

    // Add main entity schema (if not breadcrumbs)
    if (jsonLd) {
        jsonLdArray.push(jsonLd);
    }

    // WebPage schema for all pages
    jsonLdArray.push({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": canonicalUrl,
        "name": effectiveTitle,
        "description": effectiveDescription,
        "url": canonicalUrl,
        "isPartOf": {
            "@type": "WebSite",
            "@id": SITE_URL + "/#website"
        },
        "inLanguage": htmlLang
    });

    // The homepage FAQPage schema is emitted by Catalog.js from the translated
    // catalog.faqSchema entries, so it is deliberately not duplicated here:
    // Google requires a single FAQPage entity per page, and two blocks on the
    // same URL are a duplicate-schema signal. The country-specific block below
    // is separate and covers /countries/:code.

            // Country-specific FAQ schema
            if (path.startsWith('/countries/') && path.length > '/countries/'.length) {
                const countryCode = path.substring('/countries/'.length).toUpperCase();
                const countryName = COUNTRY_NAMES[countryCode] || countryCode;
                jsonLdArray.push({
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": [
                        {
                            "@type": "Question",
                            "name": `What are the key economic indicators for ${countryName}?`,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": `The key economic indicators for ${countryName} include GDP growth, inflation rate, unemployment rate, exchange rate, and government debt levels. These metrics provide insights into the country's economic performance and stability.`
                            }
                        },
                        {
                            "@type": "Question",
                            "name": `How can I compare ${countryName}'s economy with other countries?`,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": `You can compare ${countryName}'s economic indicators with other countries using our comparison tool. Simply select ${countryName} as one country and choose another country to view side-by-side data on GDP, inflation, wages, and other metrics.`
                            }
                        },
                        {
                            "@type": "Question",
                            "name": `Where does the economic data for ${countryName} come from?`,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": `The economic data for ${countryName} is sourced from official institutions such as the central bank, national statistics office, IMF, World Bank, and other authoritative sources. All data is regularly updated to ensure accuracy.`
                            }
                        }
                    ]
                });
            }

    // HowTo schema for the comparison workflow (AEO)
        if (path === '/compare') {
            jsonLdArray.push({
                "@context": "https://schema.org",
                "@type": "HowTo",
                "name": "How to Compare Economic Indicators Between Two Countries",
                "description": "Step-by-step guide to comparing GDP, inflation, and wage data between two countries using WecoBook's comparison tool.",
                "step": [
                    {
                        "@type": "HowToStep",
                        "position": 1,
                        "name": "Open the Comparison Tool",
                        "text": "Navigate to the Country Economic Comparison page from the main navigation."
                    },
                    {
                        "@type": "HowToStep",
                        "position": 2,
                        "name": "Select the First Country",
                        "text": "Choose the first country you want to analyze from the Country A dropdown."
                    },
                    {
                        "@type": "HowToStep",
                        "position": 3,
                        "name": "Select the Second Country",
                        "text": "Choose the second country from the Country B dropdown to load side-by-side data."
                    },
                    {
                        "@type": "HowToStep",
                        "position": 4,
                        "name": "Analyze the Indicators",
                        "text": "Review GDP growth, inflation, minimum wage, and average wage metrics displayed side-by-side."
                    }
                ]
            });
        }

    return (
        <Helmet htmlAttributes={{ lang: htmlLang }}>
            <meta charSet="utf-8" />
            {/* Bing (and therefore Yahoo and DuckDuckGo) treats hreflang as a
                secondary signal and reads the content-language declaration
                primarily, alongside <html lang>. */}
            <meta httpEquiv="content-language" content={htmlLang} />
            {!defersPageMeta && <title>{effectiveTitle}</title>}
            {!defersPageMeta && <meta name="description" content={effectiveDescription} />}
            <link rel="canonical" href={canonicalUrl} />
            {languageAlternates.map(({ lng, href }) => (
                <link key={lng} rel="alternate" hrefLang={HTML_LANG[lng]} href={href} />
            ))}
            <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${localizePath(path, DEFAULT_LANGUAGE)}`} />
            <meta name="robots" content="index, follow" />
            <meta name="author" content={effectiveAuthor} />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <meta name="keywords" content={effectiveKeywords} />
            <meta name="google-adsense-account" content="ca-pub-1076544393536932" />
            <meta property="og:type" content={effectiveType} />
            {!defersPageMeta && <meta property="og:title" content={effectiveTitle} />}
            {!defersPageMeta && <meta property="og:description" content={effectiveDescription} />}
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:site_name" content="WecoBook" />
            <meta property="og:locale" content={OG_LOCALE[language]} />
            {SUPPORTED_LANGUAGES
                .filter((lng) => lng !== language)
                .map((lng) => (
                    <meta key={lng} property="og:locale:alternate" content={OG_LOCALE[lng]} />
                ))}
            <meta property="og:image" content={`${SITE_URL}/og-image.jpg`} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={`${effectiveTitle} — WecoBook`} />
            <meta name="twitter:creator" content={effectiveAuthor} />
            <meta name="twitter:card" content="summary_large_image" />
            {!defersPageMeta && <meta name="twitter:title" content={effectiveTitle} />}
            {!defersPageMeta && <meta name="twitter:description" content={effectiveDescription} />}
            <meta name="twitter:image" content={`${SITE_URL}/og-image.jpg`} />
            <meta name="twitter:image:alt" content={`${effectiveTitle} — WecoBook`} />
            {jsonLdArray.length > 0 && jsonLdArray.map((schema, index) => (
                <script type="application/ld+json" key={`jsonld-${index}`}>
                    {JSON.stringify(schema, null, 2)}
                </script>
            ))}
        </Helmet>
    )
}